import React from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as CoinIcon } from '../../assets/icons/coin.svg';
import RewardBadge from '../RewardBadge';

function clampInt(n) {
    const v = Math.floor(Number(n));
    if (!Number.isFinite(v)) return 0;
    return Math.max(0, v);
}

export default function StockDialog({
    open,
    onClose,
    onConfirm,
    product,
    currentStock = 0,
    storeId,
}) {
    const [operation, setOperation] = React.useState('add');
    const [amount, setAmount] = React.useState(1);
    const isAlreadyZero = currentStock <= 0 && operation === 'remove';

    React.useEffect(() => {
        if (!open) return;
        setOperation('add');
        setAmount(1);
    }, [open]);

    const handleChangeOperation = (_, value) => {
        if (!value) return;
        setOperation(value);
        if (value === 'remove') setAmount(currentStock > 0 ? currentStock : 0);
        else if (amount <= 0) setAmount(1);
    };

    const parsed = clampInt(amount);

    const previewStock =
        operation === 'add'
            ? currentStock + parsed
            : operation === 'reduce'
              ? Math.max(0, currentStock - parsed)
              : 0;

    const invalidAmount =
        operation !== 'remove' &&
        (parsed <= 0 || (operation === 'reduce' && parsed > currentStock));

    const confirmDisabled = operation !== 'remove' ? invalidAmount : false;

    const handleConfirm = () => {
        const payload = {
            storeId: storeId ?? product?.storeId,
            productId: product?.id ?? product?.productId,
            stockId: storeId,
            operation:
                operation === 'add'
                    ? 'ADD'
                    : operation === 'reduce'
                      ? 'SUBTRACT'
                      : 'CLEAR',
            amount: operation === 'remove' ? Math.max(1, currentStock) : parsed,
        };
        onConfirm(payload);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pr: 6 }} color="#0C3466">
                Ajustar estoque
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ position: 'absolute', right: 12, top: 12 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Stack
                    direction="row"
                    spacing={2}
                    alignItems="start"
                    justifyContent={'space-between'}
                    sx={{ mb: 2 }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                            variant="rounded"
                            src={
                                product?.imageUrl ||
                                product?.photoUrl ||
                                undefined
                            }
                            alt={product?.name}
                            sx={{ width: 64, height: 64, borderRadius: 2 }}
                        >
                            {(product?.name || '?')[0]}
                        </Avatar>

                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" noWrap>
                                {product?.name}
                            </Typography>
                            {product?.description && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    noWrap
                                >
                                    {product.description}
                                </Typography>
                            )}
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{ mt: 0.5, ml: 1 }}
                                useFlexGap
                                flexWrap="wrap"
                            >
                                {typeof product?.price === 'number' && (
                                    <RewardBadge
                                        icon={
                                            <CoinIcon
                                                style={{
                                                    color: '#134F99',
                                                    fontSize: '24px',
                                                }}
                                            />
                                        }
                                        value={product?.price}
                                    />
                                )}
                                {product?.statusLabel && (
                                    <Chip
                                        size="medium"
                                        label={product.statusLabel}
                                        color="success"
                                    />
                                )}
                            </Stack>
                        </Box>
                    </Stack>

                    <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary">
                            Estoque atual
                        </Typography>
                        <Typography variant="h5" fontWeight={700}>
                            {currentStock}
                        </Typography>
                    </Box>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <FormControl fullWidth>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Operação
                    </Typography>
                    <ToggleButtonGroup
                        value={operation}
                        exclusive
                        onChange={handleChangeOperation}
                        fullWidth
                        color="primary"
                    >
                        <ToggleButton value="add" sx={{ py: 1.2 }}>
                            <AddCircleOutlineIcon fontSize="small" />
                            <Box component="span" sx={{ ml: 1 }}>
                                Adicionar
                            </Box>
                        </ToggleButton>
                        <ToggleButton value="reduce" sx={{ py: 1.2 }}>
                            <RemoveCircleOutlineIcon fontSize="small" />
                            <Box component="span" sx={{ ml: 1 }}>
                                Reduzir
                            </Box>
                        </ToggleButton>
                        <ToggleButton value="remove" sx={{ py: 1.2 }}>
                            <DeleteOutlineIcon fontSize="small" />
                            <Box component="span" sx={{ ml: 1 }}>
                                Remover da loja
                            </Box>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </FormControl>

                {operation !== 'remove' && (
                    <TextField
                        label="Quantidade"
                        type="number"
                        inputProps={{ min: 1 }}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        sx={{ mt: 2 }}
                        fullWidth
                        error={invalidAmount}
                        helperText={
                            invalidAmount
                                ? operation === 'reduce'
                                    ? 'Não é possível reduzir mais do que o estoque atual.'
                                    : 'Informe um número inteiro maior que zero.'
                                : ' '
                        }
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    {operation === 'add' ? '+' : '-'}
                                </InputAdornment>
                            ),
                        }}
                    />
                )}

                {operation === 'remove' && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Esta ação definirá o estoque para <b>0</b> e a
                        recompensa não será visível para os jogadores até que
                        sejam adicionados mais items ao estoque.
                    </Alert>
                )}

                {operation !== 'remove' && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'background.default',
                            border: (t) => `1px solid ${t.palette.divider}`,
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="body2" color="text.secondary">
                                Estoque após a operação:
                            </Typography>
                            <Typography variant="h6" fontWeight={700}>
                                {previewStock}
                            </Typography>
                            <Tooltip title="Pré-visualização do resultado">
                                <Chip
                                    size="small"
                                    label="Prévia"
                                    variant="outlined"
                                />
                            </Tooltip>
                        </Stack>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="outlined">
                    Cancelar
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color={operation === 'remove' ? 'error' : 'primary'}
                    disabled={
                        confirmDisabled ||
                        !(storeId ?? product?.storeId) ||
                        isAlreadyZero
                    }
                >
                    {operation === 'add' && 'Adicionar ao estoque'}
                    {operation === 'reduce' && 'Reduzir estoque'}
                    {operation === 'remove' && 'Remover da loja'}
                    {operation === 'remove' && currentStock === 0 && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Este produto já está com estoque <b>0</b>.
                        </Alert>
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
